import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import AppButton from "../../Common/AppButton";
import { checkReviseAnswer } from "../../Utils/AssertionLogic";
import axios from "axios";
import { useAuth } from "../../../providers/auth-provider";
import mixpanel from 'mixpanel-browser';

const VerticalSwiper = ({
  questions,
  setValue,
  uniqueSubTopics,
  answers,
  setAnswers,
  filterOption,
}) => {
  const { user } = useAuth();
  const [selectedOption, setSelectedOption] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState(null);

  // has the current question been submitted?
  const isAnswered = Boolean(answers[activeIndex]?.submitted);

  //
  // 1) Whenever we get a Swiper instance, jump to `activeIndex`
  // 2) Whenever `activeIndex` changes, jump there again
  //
  useEffect(() => {
    if (swiperInstance) {
      swiperInstance.slideTo(activeIndex);
    }
  }, [activeIndex, swiperInstance]);

  //
  // 3) When the filterOption prop flips,
  //    reset our state to slide #0 and then jump there
  //
  useEffect(() => {
    if (!filterOption) return;
    setActiveIndex(0);
    if (swiperInstance) {
      swiperInstance.slideTo(0);
    }
  }, [filterOption, swiperInstance]);

  const handleOptionChange = (option) => {
    if (!isAnswered) setSelectedOption(option);
  };

  const handleChange = (newIndex) => {
    setActiveIndex(newIndex);
    setSelectedOption(null);

    const sub = questions[newIndex].subTopic;
    const idx = uniqueSubTopics.findIndex((s) => s === sub) + 1;
    setValue(idx);
  };

  const handleResetAnswer = () => {
    mixpanel.identify(user.id);
    mixpanel.track('Revise_Card_action', {
      action: 'tryAgain',
    });
    const copy = [...answers];
    copy[activeIndex] = { submitted: false, isCorrect: false };
    setAnswers(copy);
    setSelectedOption(null);

    const current = questions[activeIndex];
    console.log(current, "current");
    mixpanel.identify(user.id);
    mixpanel.track('Revise_Card_Action', {
      action: 'tryAgain',
      skill: current.skill,
      topic: current.topic,
      question: current.id,
    });
  };

  const handleSubmitAnswer = async (answer, isTimerEnd = false) => {



    const trimmed = answer?.trim();
    if (!trimmed && !isTimerEnd) return;

    const current = questions[activeIndex];
    const correct = checkReviseAnswer(
      current.assertionLogic,
      trimmed,
      current
    );

    const copy = [...answers];
    copy[activeIndex] = {
      submitted: true,
      isCorrect: correct,
      selectedOption: answer,
    };
    setAnswers(copy);

    console.log(current, "current");
    mixpanel.identify(user.id);
    mixpanel.track('Revise_Card_Action', {
      action: 'submit',
      skill: current.skill,
      topic: current.topic,
      question: current.id,
    });
    try {
      await axios.post(
        `${process.env.REACT_APP_NODE_BASE_URL}/practice-arena/revise-response`,
        {
          userId: user.id,
          questionId: current.id,
          answer: trimmed,
          isCorrect: correct,
          topic: current.topic,
          skill: current.skill,
          subject: current.subject,
          options: current.options,
        }
      );
    } catch (e) {
      console.error("Failed to store response:", e);
    }
  };

  const getOptionStyle = (option) => {
    const isSelected = option === selectedOption;
    return {
      border: `2px solid ${isSelected ? "#ccf900" : "gray"}`,
      backgroundColor: isSelected ? "rgba(204, 249, 0, 0.4)" : "#fff",
      borderRadius: "5px",
      padding: "10px 20px",
      margin: "10px auto",
      width: "100%",
      textAlign: "center",
      color: isSelected ? "white" : "black",
      cursor: !isAnswered ? "pointer" : "default",
      fontSize: "14px",
    };
  };

  return (
    <Swiper
      direction="vertical"
      slidesPerView={1.2}
      loop={false}
      speed={600}
      style={{ height: "75vh", width: "85vw" }}
      initialSlide={activeIndex}
      onSwiper={setSwiperInstance}
      onSlideChange={(swiper) => handleChange(swiper.activeIndex)}
      spaceBetween={20}
    >
      {questions.map((question, idx) => (
        <SwiperSlide
          key={idx}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            background: activeIndex === idx ? "#252D00" : "#799400",
            color: "white",
            borderRadius: "15px",
            width: "85vw",
            marginBottom: "20px",
          }}
        >
          <div className="relative flex flex-col items-center w-full h-full pt-16 px-4">
            {/* Question Number */}
            <div className="absolute top-4 right-4 text-sm">
              {idx + 1}/{questions.length}
            </div>
            {/* Skill Display */}
            <div className="absolute top-4 left-4 text-sm">
              {question.skill}
            </div>
            {/* Question Text */}
            <div className="font-light text-base text-left w-full">
              {question.question}
            </div>
            {/* Options or Feedback */}
            {!isAnswered ? (
              <div className="mt-6 w-full">
                {question.options.map((opt) => (
                  <div
                    key={opt}
                    style={getOptionStyle(opt)}
                    onClick={() => handleOptionChange(opt)}
                    className="h-[30px] flex items-center justify-center"
                  >
                    {opt}
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-full">
                <p>Your Answer: {answers[activeIndex].selectedOption}</p>
                <p>
                  Result:{" "}
                  {answers[activeIndex].isCorrect ? "Correct" : "Incorrect"}
                </p>
                {question.explanation && (
                  <>
                    <p className="mt-4 font-semibold">Explanation:</p>
                    <div>{question.explanation}</div>
                  </>
                )}
              </div>
            )}
            {/* Submit / Try Again */}
            {idx === activeIndex && (
              <div className="fixed bottom-5 flex gap-4">
                {!isAnswered ? (
                  <AppButton
                    variant="rectangularPrimary"
                    onClick={() => handleSubmitAnswer(selectedOption)}
                  >
                    Submit
                  </AppButton>
                ) : (
                  <AppButton
                    variant="rectangularPrimary"
                    onClick={handleResetAnswer}
                  >
                    Try Again
                  </AppButton>
                )}
              </div>
            )}
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default VerticalSwiper;
